import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import DownloadIcon from "@mui/icons-material/Download";
import {
  RowData,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Stack } from "@mui/system";
import { Button, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useTranslation, useLocale } from "@/lib/i18n";
import Logo from "@/assets/images/logo-nl.svg";
import {
  EarlyWarningTableWidgetConfiguration,
  EarlyWarningTableWidgetData,
  EarlyWarningTableWidgetDataWarningsInnerPhenomenonEnum,
} from "@/lib/api";
import useRefreshWidget from "@/features/shared/widgets/hooks/useRefetchWidget";

import EarlyWarningTableCell from "./early-warning-table-cell";
import EarlyWarningTableHeaderDate from "./early-warning-table-header";
import EarlyWarningInfo from "./early-warning-info";
import EarlyWarningToday from "./early-warning-today";
import EarlyWarningMap from "./early-warning-map";
import EarlyWarningTableMapRow from "./early-warning-table-map-row";
import Styled from "./styles";
import { sortWarningsByLevel } from "./utils";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends RowData, TValue> {
    // options not used but makes eslint happy
    options?: [TData, TValue];
    fullColumn?: boolean;
  }
}

interface EarlyWarningTableProps {
  id: string;
  data: EarlyWarningTableWidgetData;
  configuration: EarlyWarningTableWidgetConfiguration;
}

export enum EarlyWarningTableToggles {
  TABLE = "table",
  MAP = "map",
}

const EarlyWarningTable = ({ id, data, configuration }: EarlyWarningTableProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  useRefreshWidget(id);
  const [toggle, setToggle] = useState<EarlyWarningTableToggles>(
    EarlyWarningTableToggles.TABLE,
  );
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedWarning, setSelectedWarning] = useState<string | undefined>(undefined);

  const columnHelper = createColumnHelper<{
    phenomenom: string;
    [date: string]: ReactNode;
  }>();

  const handleSelectDay = useCallback(
    (index: number) => {
      setSelectedWarning(undefined);
      if (toggle === EarlyWarningTableToggles.MAP) {
        setSelectedDay(index);
      }
    },
    [setSelectedDay, toggle],
  );

  const handleSelectWarningCallback = useCallback(
    (id: string, dayIndex?: number) => {
      setSelectedWarning((prev) => (prev === id ? undefined : id));
      if (dayIndex !== undefined) {
        setSelectedDay(dayIndex);
      }
    },
    [setSelectedWarning],
  );
  const handleSelectWarning = useCallback(
    (id: string, dayIndex: number) => () => handleSelectWarningCallback(id, dayIndex),
    [handleSelectWarningCallback],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.phenomenom, {
        id: "type",
        header: () => <></>,
        cell: (info) => (
          <Styled.TypeElement>
            <span>
              {t(
                `ewt.phenomena.${
                  info.getValue() as EarlyWarningTableWidgetDataWarningsInnerPhenomenonEnum
                }`,
              )}
            </span>
            <img
              src={`/icons/phenomena_${
                info.getValue() as EarlyWarningTableWidgetDataWarningsInnerPhenomenonEnum
              }.svg`}
            />
          </Styled.TypeElement>
        ),
      }),
      ...data.table.days.map((day, index) =>
        columnHelper.accessor((row) => row[day.date], {
          id: day.date,
          header: () => (
            <EarlyWarningTableHeaderDate
              date={new Date(day.date).toString()}
              narrow={toggle === EarlyWarningTableToggles.MAP}
              onClick={() => handleSelectDay(index)}
            />
          ),
          cell: (info) => info.renderValue(),
        }),
      ),
    ],
    [data, setSelectedDay, toggle],
  );

  const warnings = useMemo(() => {
    return data.warnings;
  }, [data]);

  const tableData = useMemo(() => {
    const { table } = data;

    const tableData = table.current_phenomena.map((phe) => {
      const row: {
        phenomenom: string;
        [date: string]: ReactNode;
      } = {
        phenomenom: phe,
      };
      table.days.forEach((day, dayIndex) => {
        const dayWarnings = warnings.filter(
          (warning) => phe === warning.phenomenon && day.warning_ids.includes(warning.id),
        );
        const sortedDayWarnings = sortWarningsByLevel(dayWarnings);

        row[day.date] =
          sortedDayWarnings.length > 0 ? (
            <Tooltip
              title={
                toggle === EarlyWarningTableToggles.MAP ? (
                  <>
                    {sortedDayWarnings.map((warning) => (
                      <Styled.BodyCellTooltip
                        key={`${day.date}-${phe}-${warning.level}-${warning.id}`}
                      >
                        <EarlyWarningTableCell
                          level={warning.level}
                          percentage={warning.probability_percentage}
                          multiple
                          variant={EarlyWarningTableToggles.TABLE}
                          onClick={handleSelectWarning(warning.id, dayIndex)}
                        />
                      </Styled.BodyCellTooltip>
                    ))}
                  </>
                ) : undefined
              }
            >
              <div>
                {sortedDayWarnings.map((warning) => (
                  <EarlyWarningTableCell
                    key={`${day.date}-${phe}-${warning.level}-${warning.id}`}
                    level={warning.level}
                    percentage={warning.probability_percentage}
                    multiple={sortedDayWarnings.length > 1}
                    variant={toggle}
                    onClick={handleSelectWarning(warning.id, dayIndex)}
                  />
                ))}
              </div>
            </Tooltip>
          ) : (
            <EarlyWarningTableCell multiple={false} variant={toggle} />
          );
      });
      return row;
    });

    return tableData;
  }, [data, warnings, toggle, selectedWarning]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const printRef = useRef<HTMLDivElement | null>(null);
  const print = useReactToPrint({
    contentRef: printRef,
  });

  const updatedAt = useMemo(() => {
    const timestamps = [
      data.comment.updated_at,
      ...data.warnings.map((it) => it.updated_at),
    ].map((it) => new Date(it));

    return timestamps
      .sort((a, b) => {
        return +a - +b;
      })
      .reverse()[0];
  }, [data]);

  return (
    <Styled.Widget ref={printRef}>
      <Stack
        direction="row"
        justifyContent="space-between"
        mb={2}
        gap={1}
        flexWrap="wrap"
        sx={{ width: "100%" }}
      >
        <Stack direction="column" justifyContent="center">
          <Styled.PrintTitle variant="h6" color="black" fontWeight={700}>
            KNMI Risicobeeld
          </Styled.PrintTitle>
          <div style={{ opacity: 0.5 }}>
            <Typography variant="body2" color="Black">
              {t("ewt.description")}
            </Typography>
            <Typography variant="body2" color="Black" fontStyle="italic">
              {t("widgets.meta.updatedAtBy", {
                date: format(new Date(updatedAt), "E d MMM yyyy (HH:mm)", {
                  locale: getDateFnsLocale(locale),
                }),
                source: data.comment.updated_by.name,
              })}
            </Typography>
          </div>
        </Stack>

        <Styled.Logo>
          <Logo />
        </Styled.Logo>

        <EarlyWarningToday />

        <Styled.WarningButtons
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="flex-end"
          justifyItems="flex-end"
        >
          {toggle === EarlyWarningTableToggles.TABLE && (
            <Styled.PrintButton
              variant="contained"
              size="small"
              color="primary"
              onClick={print}
              endIcon={<DownloadIcon />}
              disableRipple={true}
            >
              Save as PDF
            </Styled.PrintButton>
          )}

          <Styled.Toggle size="small">
            <Button
              size="small"
              onClick={() => setToggle(EarlyWarningTableToggles.TABLE)}
              variant={toggle === EarlyWarningTableToggles.TABLE ? "contained" : undefined}
            >
              Tabel
            </Button>
            <Button
              size="small"
              onClick={() => setToggle(EarlyWarningTableToggles.MAP)}
              variant={toggle === EarlyWarningTableToggles.MAP ? "contained" : undefined}
            >
              Kaart
            </Button>
          </Styled.Toggle>
        </Styled.WarningButtons>
      </Stack>

      {toggle === EarlyWarningTableToggles.TABLE && (
        <Styled.Wrapper direction="row" gap={2}>
          <Styled.TableWrapper>
            <Styled.Table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Styled.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Styled.HeaderCell key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Styled.HeaderCell>
                    ))}
                  </Styled.Row>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <Styled.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Styled.BodyCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Styled.BodyCell>
                    ))}
                  </Styled.Row>
                ))}

                {/* Maps */}
                <EarlyWarningTableMapRow
                  days={data.table.days}
                  warnings={warnings}
                  setToggle={setToggle}
                  setSelectedDay={setSelectedDay}
                />

                {/* Legend */}
                <Styled.Row>
                  <td></td>
                  <td colSpan={data.table.days.length}>
                    <Styled.Legend>
                      <Styled.LegendItem>
                        <Styled.LegendItemColor level="green" />
                        <span>
                          {"<"} 10% <strong>kans op</strong> waarschuwing
                        </span>
                      </Styled.LegendItem>
                      <Styled.LegendItem>
                        <Styled.LegendItemColor level="yellow" />
                        <span>
                          {"≥"} 30% <strong>kans op</strong> waarschuwing code geel
                        </span>
                      </Styled.LegendItem>
                      <Styled.LegendItem>
                        <Styled.LegendItemColor level="orange" />
                        <span>
                          {"≥"} 10% <strong>kans op</strong> waarschuwing code oranje
                        </span>
                      </Styled.LegendItem>
                    </Styled.Legend>
                  </td>
                </Styled.Row>
              </tbody>
            </Styled.Table>
          </Styled.TableWrapper>

          <EarlyWarningInfo content={data.comment.content ?? ""} />
        </Styled.Wrapper>
      )}

      {toggle === EarlyWarningTableToggles.MAP && (
        <EarlyWarningMap
          table={table}
          selectedDay={selectedDay}
          setSelectedDay={handleSelectDay}
          warnings={warnings.filter((warning) =>
            data.table.days[selectedDay].warning_ids.includes(warning.id),
          )}
          selectedWarningId={selectedWarning}
          setSelectedWarning={handleSelectWarningCallback}
          layers={configuration.layers}
        />
      )}
    </Styled.Widget>
  );
};

export default EarlyWarningTable;
